import { Alert, Stack, TableContainer } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { FormatCurrency, Text } from '@vestwell-frontend/ui';
import { useWentworthBalanceGetBalancesGroupedBySource } from '@vestwell-unified-api/hooks';

import { format } from 'date-fns';
import Decimal from 'decimal.js';
import { FC, useMemo } from 'react';

import {
  LastTableRowNoBorderTable,
  PrimaryTableCell,
  SecondaryTableCell
} from './BalancesTable.component';
import { FUNDING_SOURCE_LABELS } from './consts';

export type BalanceBreakdownBySourceProps = {
  participantId: number;
};

export const BalanceBreakdownBySource: FC<
  BalanceBreakdownBySourceProps
> = props => {
  const balanceBreakdown = useWentworthBalanceGetBalancesGroupedBySource(
    {
      dateOf: format(new Date(), 'MM-dd-yyyy'),
      participantId: props.participantId
    },
    {
      query: {
        enabled: !!props.participantId,
        useErrorBoundary: true
      }
    }
  );

  const mapEmployeeDetails = useMemo(
    () =>
      [
        {
          label: 'Pre-tax',
          value: balanceBreakdown.data?.employeeDetails.preTax
        },
        {
          label: 'Roth',
          value: balanceBreakdown.data?.employeeDetails.roth
        },
        {
          label: 'After-tax',
          value: balanceBreakdown.data?.employeeDetails.afterTax
        }
      ].filter(detail => !new Decimal(detail.value || 0).isZero()),
    [balanceBreakdown.data?.employeeDetails]
  );

  const mapEmployerDetails = useMemo(
    () =>
      balanceBreakdown.data?.employerDetails
        .map(detail => ({
          label:
            FUNDING_SOURCE_LABELS[detail.fundingSource] || detail.fundingSource,
          unvestedBalance: detail.unvestedBalance,
          vestedBalance: detail.vestedBalance
        }))
        .filter(
          detail =>
            !(
              new Decimal(detail.vestedBalance || 0).isZero() &&
              new Decimal(detail.unvestedBalance || 0).isZero()
            )
        ),
    [balanceBreakdown.data?.employerDetails]
  );

  const mapRolloverDetails = useMemo(
    () =>
      [
        {
          label: 'Pre-Tax Rollover',
          value: balanceBreakdown.data?.rolloverDetails.preTax
        },
        {
          label: 'Roth Rollover',
          value: balanceBreakdown.data?.rolloverDetails.roth
        }
      ].filter(detail => !new Decimal(detail.value || 0).isZero()),
    [balanceBreakdown.data?.rolloverDetails]
  );

  return (
    <>
      <Stack mb={2}>
        <Alert role='note' severity='info'>
          You are viewing the market value of your balances, which may differ
          from the total amount contributed.
        </Alert>
      </Stack>
      <TableContainer>
        <LastTableRowNoBorderTable data-testid='breakdown-by-source-table'>
          <TableHead>
            <TableRow>
              <TableCell align='left' colSpan={2}>
                Source
              </TableCell>
              <TableCell align='right'>Market Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {balanceBreakdown.isLoading && (
              <>
                <TableRow>
                  <PrimaryTableCell colSpan={2}>
                    <Text isLoading={balanceBreakdown.isLoading} m={0} />
                  </PrimaryTableCell>
                  <PrimaryTableCell align='right'>
                    <Text isLoading={balanceBreakdown.isLoading} m={0} />
                  </PrimaryTableCell>
                </TableRow>
                <TableRow>
                  <PrimaryTableCell colSpan={2}>
                    <Text isLoading={balanceBreakdown.isLoading} m={0} />
                  </PrimaryTableCell>
                  <PrimaryTableCell align='right'>
                    <Text isLoading={balanceBreakdown.isLoading} m={0} />
                  </PrimaryTableCell>
                </TableRow>
                <TableRow>
                  <SecondaryTableCell colSpan={2}>
                    <Text isLoading={balanceBreakdown.isLoading} m={0} />
                  </SecondaryTableCell>
                  <SecondaryTableCell align='right'>
                    <Text isLoading={balanceBreakdown.isLoading} m={0} />
                  </SecondaryTableCell>
                </TableRow>{' '}
              </>
            )}
            {new Decimal(
              balanceBreakdown.data?.employeeDetails.total || 0
            ).isZero() &&
            balanceBreakdown.data?.employerDetails.every(
              detail =>
                new Decimal(detail.vestedBalance || 0).isZero() &&
                new Decimal(detail.unvestedBalance || 0).isZero()
            ) &&
            new Decimal(
              balanceBreakdown.data?.rolloverDetails.total || 0
            ).isZero() ? (
              <TableRow>
                <TableCell align='center' colSpan={2}>
                  <Text>No Data</Text>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {!new Decimal(
                  balanceBreakdown.data?.employeeDetails.total || 0
                ).isZero() && (
                  <TableRow>
                    <PrimaryTableCell colSpan={2}>
                      <Text m={0}>Employee Contribution</Text>
                    </PrimaryTableCell>
                    <PrimaryTableCell align='right'>
                      <Text m={0}>
                        <FormatCurrency
                          negativeNotation='minus'
                          precision={2}
                          stripTrailingZeros={false}
                          value={balanceBreakdown.data?.employeeDetails.total}
                        />
                      </Text>
                    </PrimaryTableCell>
                  </TableRow>
                )}
                {mapEmployeeDetails?.map((detail, index) => {
                  return (
                    <TableRow key={index}>
                      <SecondaryTableCell colSpan={2}>
                        {detail.label}
                      </SecondaryTableCell>
                      <SecondaryTableCell align='right'>
                        <FormatCurrency
                          negativeNotation='minus'
                          precision={2}
                          stripTrailingZeros={false}
                          value={detail.value}
                        />
                      </SecondaryTableCell>
                    </TableRow>
                  );
                })}
                {balanceBreakdown.data?.employerDetails.length > 0 && (
                  <>
                    <TableRow>
                      <PrimaryTableCell colSpan={2}>
                        <Text m={0}>Employer Contributions</Text>
                      </PrimaryTableCell>
                      <PrimaryTableCell align='right'>
                        <Text m={0}>
                          <FormatCurrency
                            negativeNotation='minus'
                            precision={2}
                            stripTrailingZeros={false}
                            value={balanceBreakdown.data?.employerDetails.reduce(
                              (sum, detail) =>
                                sum +
                                detail.vestedBalance +
                                detail.unvestedBalance,
                              0
                            )}
                          />
                        </Text>
                      </PrimaryTableCell>
                    </TableRow>
                    <TableRow>
                      <SecondaryTableCell>
                        <Text fontWeight='bold' m={0}>
                          Source
                        </Text>
                      </SecondaryTableCell>
                      <SecondaryTableCell align='right'>
                        <Text fontWeight='bold' m={0}>
                          Vested
                        </Text>
                      </SecondaryTableCell>
                      <SecondaryTableCell align='right'>
                        <Text fontWeight='bold' m={0}>
                          Unvested
                        </Text>
                      </SecondaryTableCell>
                    </TableRow>
                    {mapEmployerDetails?.map((detail, index) => {
                      return (
                        <TableRow key={index}>
                          <SecondaryTableCell>
                            {detail.label}
                          </SecondaryTableCell>
                          <SecondaryTableCell align='right'>
                            <FormatCurrency
                              negativeNotation='minus'
                              precision={2}
                              stripTrailingZeros={false}
                              value={detail.vestedBalance}
                            />
                          </SecondaryTableCell>
                          <SecondaryTableCell align='right'>
                            <FormatCurrency
                              negativeNotation='minus'
                              precision={2}
                              stripTrailingZeros={false}
                              value={detail.unvestedBalance}
                            />
                          </SecondaryTableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
                {!new Decimal(
                  balanceBreakdown.data?.rolloverDetails.total || 0
                ).isZero() && (
                  <TableRow>
                    <PrimaryTableCell colSpan={2}>
                      <Text m={0}>Rollovers</Text>
                    </PrimaryTableCell>
                    <PrimaryTableCell align='right'>
                      <Text m={0}>
                        <FormatCurrency
                          negativeNotation='minus'
                          precision={2}
                          stripTrailingZeros={false}
                          value={balanceBreakdown.data?.rolloverDetails.total}
                        />
                      </Text>
                    </PrimaryTableCell>
                  </TableRow>
                )}
                {mapRolloverDetails?.map((detail, index) => {
                  return (
                    <TableRow key={index}>
                      <SecondaryTableCell colSpan={2}>
                        {detail.label}
                      </SecondaryTableCell>
                      <SecondaryTableCell align='right'>
                        <FormatCurrency
                          negativeNotation='minus'
                          precision={2}
                          stripTrailingZeros={false}
                          value={detail.value}
                        />
                      </SecondaryTableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </LastTableRowNoBorderTable>
      </TableContainer>
    </>
  );
};

BalanceBreakdownBySource.displayName = 'BalanceBreakdownBySource';
